import { StandardEvents } from '@shopify/events';

export function idOf(id) { return String(id ?? '').split('/').pop(); }
export function offerApplies(item, cfg) {
  return cfg.giftIds.includes(idOf(item.variant_id)) && item.quantity > 0 &&
    item.line_level_discount_allocations?.some(a =>
      a.discount_application?.title === cfg.title &&
      a.discount_application?.value_type === 'percentage' &&
      Number(a.discount_application?.value) === 50 && Number(a.amount) > 0);
}
export function canOpen(items, addedIds, cfg) {
  return items.some(i => cfg.cakeIds.includes(idOf(i.variant_id)) && addedIds.includes(idOf(i.variant_id)) &&
    i.quantity > 0 && i.final_price === i.original_price) &&
    !items.some(i => cfg.giftIds.includes(idOf(i.variant_id)) && i.quantity > 0);
}
export function install(cfg) {
  if (!cfg?.enabled || window.__pvKlaviyoGiftInstalled) return;
  window.__pvKlaviyoGiftInstalled = true;
  const preview = cfg.preview || window.Shopify?.theme?.role !== 'main' ||
    window.Shopify?.designMode || new URLSearchParams(location.search).has('preview_theme_id');
  const key = 'pv:' + cfg.campaign + (preview ? ':preview' : ':live');
  const consent = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  const active = () => Date.now() >= Date.parse(cfg.startsAt) && Date.now() < Date.parse(cfg.endsAt) && cfg.currency === 'GBP';
  let state = {};
  const readState = () => {
    if (!consent()) return {};
    try { const s = JSON.parse(sessionStorage.getItem(key) || '{}'); return Date.now() - (s.seenAt || 0) < 1800000 ? s : {}; }
    catch { return {}; }
  };
  state = readState();
  const save = () => { if (consent()) { try { sessionStorage.setItem(key, JSON.stringify(state)); } catch {} } };
  let lastItems = cfg.items || [];
  let opening = false;
  let shownThisPage = false;
  let requestPending = false;
  let viewTrackedThisPage = false;
  const track = (name, extra = {}) => {
    const params = { form_id: cfg.formId, promotion_id: cfg.campaign,
      promotion_name: 'Birthday cake half-price letterbox gift', creative_slot: 'klaviyo_popup', ...extra };
    if (preview) { console.info('[PV popup preview]', JSON.stringify({ event: name, ...params })); return; }
    if (!cfg.analytics || !consent() || typeof window.gtag !== 'function') return;
    window.gtag('event', name, { ...params, send_to: cfg.measurementId, transport_type: 'beacon' });
  };
  const itemParams = i => ({ item_id: String(i.sku || i.variant_id), item_name: i.product_title || i.title,
    item_variant: idOf(i.variant_id), price: offerApplies(i, cfg) ? (Number(i.original_price) - i.line_level_discount_allocations.filter(a => a.discount_application?.title === cfg.title).reduce((n,a) => n + Number(a.amount), 0)) / 100 : Number(i.final_price) / 100, quantity: 1,
    promotion_id: cfg.campaign });
  window.addEventListener('visitorConsentCollected', () => {
    if (!consent()) { state = {}; try { sessionStorage.removeItem(key); } catch {} }
  });
  window.addEventListener('klaviyoForms', event => {
    if (event.detail?.formId !== cfg.formId || !active()) return;
    if (event.detail.type === 'open') {
      shownThisPage = true; opening = false; requestPending = false;
      if (consent()) { state = { ...state, seenAt: Date.now() }; save(); }
      if (!viewTrackedThisPage) { track('view_promotion'); viewTrackedThisPage = true; }
    } else if (event.detail.type === 'redirectedToUrl') {
      if (!shownThisPage && !state.seenAt) return;
      if (!state.clicked) { track('select_promotion'); state.clicked = true; save(); }
    } else if (event.detail.type === 'close') {
      if (shownThisPage) track('pv_popup_dismiss');
    }
  });
  async function openAfterAdd(items, addedIds) {
    if (!active() || opening || shownThisPage || !canOpen(items, addedIds, cfg)) return;
    opening = true;
    try {
      await new Promise(resolve => setTimeout(resolve, 350));
      if (!active()) return;
      const r = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { cache: 'no-store' });
      if (!r.ok) return;
      const cart = await r.json();
      if (cart.currency !== 'GBP' || cart.cart_level_discount_applications?.length || !canOpen(cart.items, addedIds, cfg)) return;
      lastItems = cart.items;
      const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
      if (drawer?.isOpen && typeof drawer.close === 'function') await drawer.close();
      if (document.querySelector('dialog:modal')) return;
      requestPending = true;
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', cfg.formId]);
      console.info('[PV popup trigger]', 'Requested after successful birthday cake add');
    } catch (error) { console.warn('[PV popup trigger]', 'Could not open offer'); }
    finally { opening = false; }
  }
  document.addEventListener(StandardEvents.cartLinesUpdate, event => {
    if (!active() || !event.promise) return;
    const addedIds = (event.lines || []).map(l => idOf(l.merchandiseId));
    event.promise.then(({ detail }) => {
      if (detail?.didError || !Array.isArray(detail?.items)) return;
      const items = detail.items;
      if (consent()) state = { ...readState(), ...state };
      if (event.action === 'add' && state.seenAt && Date.now() - state.seenAt < 1800000) {
        for (const item of items) {
          if (!addedIds.includes(idOf(item.variant_id)) || !cfg.giftIds.includes(idOf(item.variant_id))) continue;
          const before = lastItems.filter(i => idOf(i.variant_id) === idOf(item.variant_id)).reduce((n,i) => n + i.quantity, 0);
          const after = items.filter(i => idOf(i.variant_id) === idOf(item.variant_id)).reduce((n,i) => n + i.quantity, 0);
          if (after <= before) continue;
          if (!state.giftAdded) { track('pv_popup_gift_added', { currency: cfg.currency, items: [itemParams(item)] }); state.giftAdded = true; }
          if (!state.offerApplied && offerApplies(item, cfg)) {
            track('pv_popup_offer_applied', { currency: cfg.currency, value: itemParams(item).price,
              coupon: cfg.title, items: [itemParams(item)], attribution: state.clicked ? 'clicked_popup' : 'viewed_popup' });
            state.offerApplied = true;
          }
        }
        save();
      }
      lastItems = items;
      if (event.action === 'add' && !requestPending) void openAfterAdd(items, addedIds);
    }).catch(() => {});
  });
  document.addEventListener('click', event => {
    if (!active() || !consent() || !state.seenAt || Date.now() - state.seenAt >= 1800000 || state.checkout) return;
    if (!event.target.closest?.('[name="checkout"],a[href="/checkout"]')) return;
    const gift = lastItems.find(i => offerApplies(i, cfg));
    if (!gift) return;
    track('pv_popup_checkout', { currency: cfg.currency, coupon: cfg.title, items: [itemParams(gift)] });
    state.checkout = true; save();
  });
}
const config = document.getElementById('pv-klaviyo-gift-config');
if (config) { try { install(JSON.parse(config.textContent)); } catch (error) { console.warn('[PV popup]', 'Configuration unavailable'); } }
